/**
 * @file index.tsx
 * @author liuchen(loocliu@tencent.com)
 */
import classNames from 'classnames';
import { isEmpty } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import { StarIcon } from 'tdesign-icons-react';
import { Button, MessagePlugin } from 'tdesign-react';

import { BotGeneratorIcon } from '@/components/icon';
import { IBot } from '@/interface';
import { generateBot, modifyBotDetail } from '@/services/bot';
import { useAppDispatch, useTypedSelector } from '@/store';
import { INIT_IMAGE_CONTENT } from '@/store/modules/__endpoints__/imMessage';
import { initialState, setBotLoading } from '@/store/modules/bot';

import styles from './styles/index.module.less';

interface IBotGeneratorInnerProps {
  autoSubmit: boolean;
  onComplete: (value: Partial<IBot>) => void;
  onBegin?: () => void;
  disabled?: boolean;
  focusNum?: number;
  rootStyle?: React.CSSProperties;
}

const BotGeneratorInner = (props: IBotGeneratorInnerProps) => {
  const { autoSubmit, onBegin, onComplete, focusNum, disabled = false } = props;
  const [value, setValue] = useState<string>();
  const [loading, setLoading] = useState(false);
  const [focus, setFocus] = useState(false);
  const dispatch = useAppDispatch();

  const { vuid } = useTypedSelector(store => store.user);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const parseRes = useCallback((res: string) => {
    const arr1 = res.split(';');
    const botInfo: { [key: string]: any } = {};
    arr1.forEach((item) => {
      const [key, value] = item.split('=');
      if (key === 'setting') {
        Object.assign(botInfo, { phasesNew: [{ character: value }] });
      } if (key === 'instruction') {
        Object.assign(botInfo, { userInstructionBase: value });
      } else {
        Object.assign(botInfo, { [key]: value });
      }
    });
    return botInfo;
  }, []);

  const handleClick2 = useCallback(() => {
    if (!value || value.trim().length === 0) {
      return;
    }
    setLoading(true);
    onBegin?.();
    generateBot(value).then((res) => {
      if (res && !isEmpty(res)) {
        const bot: Partial<IBot> = {
          ...initialState.botInfo,
          ...parseRes(res),
          avatar: INIT_IMAGE_CONTENT,
          chatBackground: INIT_IMAGE_CONTENT,
        };
        modifyBotDetail(bot).then((res) => {
          const { code } = res;
          if (code !== 0) {
            MessagePlugin.error('生成失败，请重试');
            onComplete({});
            return;
          }
          onComplete({ ...bot, id: res?.data?.id, createdBy: `${vuid}` });
        });
      } else {
        MessagePlugin.error('生成失败，请重试');
        onComplete({});
      }
    })
      .finally(() => {
        setLoading(false);
      });
  }, [value, onBegin, vuid, onComplete]);

  const handleClick3 = useCallback(() => {
    if (!value || value.trim().length === 0) {
      return;
    }
    setLoading(true);
    onBegin?.();
    generateBot(value).then((res) => {
      if (res && !isEmpty(res)) {
        const bot: Partial<IBot> = {
          ...initialState.botInfo,
          ...parseRes(res),
        };
        onComplete(bot);
      }
    })
      .finally(() => {
        setLoading(false);
        dispatch(setBotLoading(false));
      });
  }, [value, onComplete]);

  const handleFocus = useCallback(() => {
    setFocus(true);
  }, []);

  const handleBlur = useCallback(() => {
    setFocus(false);
  }, []);

  useEffect(() => {
    const timer = setTimeout(() => {
      textareaRef.current?.focus();
    }, 100);
    return () => {
      clearTimeout(timer);
    };
  }, [focusNum]);

  return <div className={styles.root} style={props.rootStyle}>
    <div className={classNames({ [styles.wrapper]: 1, [styles['wrapper-focus']]: focus })}>
      <textarea
        className={styles.textarea}
        rows={2}
        ref={textareaRef}
        disabled={loading}
        placeholder='输入一句简单描述，如“一个城府很深的慈祥老师”，即可智能创作角色'
        autoFocus
        value={value}
        onChange={e => setValue(e.target.value)}
        onFocus={handleFocus}
        onBlur={handleBlur}
      />
    </div>
    <div className={styles.operations}>
      {autoSubmit && <>
        <Button icon={<BotGeneratorIcon
          style={{ marginRight: 6 }} />}
          className={styles.btn2}
          onClick={handleClick2}
          loading={loading}
          disabled={disabled}
        >
          {loading ? '创作中..' : '一键智能创作'}
        </Button>
      </>}
      {!autoSubmit && <Button
        loading={loading}
        icon={<StarIcon style={{ marginRight: 6 }} />}
        className={styles.btn2}
        onClick={handleClick3}
      >
        一键生成
      </Button>}
    </div>
  </div>;
};

export default BotGeneratorInner;
